import { MouseEvent as ReactMouseEvent } from 'react';

import { useTooltipPinnedStatus } from './store/selectors.js';
import { useSetTooltipState } from './store/use-set-tooltip-state.js';
import { useEventErrorHandler } from '../../core/hooks/useEventErrorHandler.js';
import type { Formatter } from '../../core/types/formatter.js';
import type { InternalSliceData } from '../types/internal/pie-chart.js';
import { getTooltipContent } from '../utils/get-tooltip-content.js';
/**
 * Hook that generates handlers for mouse interactions.
 * @param sliceId - Id of the slice.
 * @param data - Slice data for this slice.
 * @param formatter - Formatter to be used.
 * @returns Handlers to perform mouse interactions.
 */
export function useSliceHandlers(
  sliceId: string,
  data: InternalSliceData,
  formatter: Formatter,
) {
  const handlerCatch = useEventErrorHandler();
  const isTooltipPinned = useTooltipPinnedStatus();
  const { setTooltipStateContent, setTooltipStatePinned } =
    useSetTooltipState();

  const onMouseEnterHandler = () => {
    const newTooltipContent = getTooltipContent(
      sliceId,
      data,
      handlerCatch,
      formatter,
    );
    setTooltipStateContent(newTooltipContent);
  };

  const onMouseClickHandler = (event: ReactMouseEvent) => {
    event.stopPropagation();
    setTooltipStatePinned(!isTooltipPinned);
  };

  const onMouseLeaveHandler = () => {
    setTooltipStateContent(undefined);
  };

  return {
    onMouseEnterHandler,
    onMouseClickHandler,
    onMouseLeaveHandler,
  };
}
